import React from "react";
import { IconNames } from "@blueprintjs/icons";
import _ from "lodash";
import styled from "styled-components";

import { edgeFromEntity } from "shared-libs/helpers/formulas/modules";
import apis from "browser/app/models/apis";

import { Button } from "browser/components/atomic-elements/atoms/button/button";
import { Section } from "browser/components/atomic-elements/atoms/section/section";
import { Entity } from "shared-libs/models/entity";
import { CreateGateSheet } from "./create-gate-sheet";
import { MAX_UNGROUPED_SIZE, Query } from "shared-libs/models/query";
import { Icon } from "@blueprintjs/core";
import { HelpBlock } from "browser/components/atomic-elements/atoms/help-block/help-block";
import { FormTable } from "browser/components/atomic-elements/atoms/form-table/form-table";
import { LabelFormGroup } from "browser/components/atomic-elements/molecules/label-form-group/label-form-group";
import { CheckboxField } from "browser/components/atomic-elements/molecules/fields/checkbox-field";
import { SchemaUris } from "shared-libs/models/schema";
import { creationSourceFromWeb } from "browser/app/utils/utils";
import { Settings } from "browser/app/models/settings";

interface IGatesListProps {
  facility: Entity
}

interface IGatesListState {
  isCreateGateSheetOpen: boolean
  gates: Entity[]
  gateBeingEdited?: Entity
}

const BORDER_COLOR = "#e0e0e0"

const CheckboxWrapper = styled.div`
  height: 32px;
  padding-left: 0.75rem;
  display: flex;
  align-items: center;
  width: 100%;
  border-top: 1px solid ${BORDER_COLOR};
  justify-content: space-between;
`

const VerticalAlign = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
`

export class GatesList extends React.Component<IGatesListProps, IGatesListState> {
  private store: any
  private settings: Settings

  constructor(props: IGatesListProps) {
    super(props)
    this.store = apis.getStore()
    this.settings = apis.getSettings()
    this.state = {
      isCreateGateSheetOpen: false,
      gates: [],
    }

    this.loadGates()
  }

  public loadGates = async () => {
    const { facility } = this.props

    const filters = [
      {
        path: "core_yms_gate.facility",
        type: "matchEdge",
        value: { entityId: facility.uniqueId },
      },
    ]

    const orders = [
      {
        label: "Name",
        path: "location.name",
        type: "ascending",
      },
    ]

    const collection = new Query(apis)
      .setEntityType(SchemaUris.GATE_TYPE)
      .setFilters(filters)
      .setOrders(orders)
      .setSize(MAX_UNGROUPED_SIZE)
      .getCollection()

    const gates = await collection.find()
    this.setState({ gates })
  }

  public render() {
    const { gates, gateBeingEdited } = this.state

    const addButton = (
      <Button onClick={this.handleOpenCreateGateSheet}>
        <VerticalAlign>
          <Icon className="u-bumperRight--xs" icon={IconNames["ADD"]} />
          Add Gate
        </VerticalAlign>
      </Button>
    )

    const [activeGates, inactiveGates] = _.partition(
      gates,
      (gate: any) => gate.core_yms_gate.isActive
    )

    return (
      <>
        {this.renderSection(
          "Active Gates",
          "There are no active gates for this facility",
          activeGates,
          addButton
        )}
        {this.renderSection(
          "Deactivated Gates",
          "There are no deactivated gates for this facility",
          inactiveGates
        )}

        <CreateGateSheet
          {...this.props}
          settings={this.settings}
          isOpen={this.state.isCreateGateSheetOpen}
          onClose={this.handleCloseCreateGateSheet}
          uiSchemaPath="uiSchema.web.entityCreationSheet"
          gate={gateBeingEdited as any}
        />
      </>
    )
  }

  public renderSection(title, helpText, gates, headerControls?) {
      return (
        <Section title={title} headerControls={headerControls} headerClassName="u-bumperTop--sm u-bumperBottom--sm">
          {_.isEmpty(gates) ?
            this.renderZeroState(helpText) :
            this.renderGates(gates)
          }
        </Section>
      )
    }

  public renderGates = (gates: Entity[]) => {
    return <FormTable>{gates.map(this.renderGate)}</FormTable>
  }

  public renderGate = (gate: any) => {
    const gateName = gate.displayName
    const isActive = gate.core_yms_gate.isActive

    const gateLabel = (
      <>
        {gateName}
      </>
    )

    return (
      <LabelFormGroup
        label={gateLabel}
        labelProps={{ className: "c-label--isHorizontalVeryWide" }}
        key={gate.uniqueId}
      >
        <CheckboxWrapper>
          <CheckboxField
            label=""
            onChange={() => {
              this.flipGateActive(gate)
            }}
            value={isActive}
          />
          <a
            onClick={() => {
              this.editGate(gate)
            }}
            className="u-bumperRight--sm"
          >
            Edit
          </a>
        </CheckboxWrapper>
      </LabelFormGroup>
    )
  }

  public editGate = (gate: Entity) => {
    this.setState({
      gateBeingEdited: gate,
      isCreateGateSheetOpen: true,
    })
  }

  public flipGateActive = async (gate: any) => {
    gate.core_yms_gate.isActive = !gate.core_yms_gate.isActive
    await gate.save()
    this.loadGates()
  }

  public renderZeroState = (helpText: string) => {
    return (
      <div className="c-boxListItem c-boxListItem--noHover">
        <div className="c-boxListItem-content u-textCenter u-bumperTop--xl u-bumperBottom--xl">
          <Icon className="u-bumperTop" icon={IconNames["HOME"]} />
          <HelpBlock>{helpText}</HelpBlock>
        </div>
      </div>
    )
  }

  private handleOpenCreateGateSheet = async () => {
    const entitySchema = SchemaUris.GATE_TYPE
    const { facility } = this.props

    const entityDefaultValue = {
      core_yms_gate: {
        facility: edgeFromEntity(facility),
        isActive: true,
      },
      creationSource: creationSourceFromWeb("Gate Management - Create"),
    }

    const schemas = await this.store.getEntitySchemasWithDependencies([entitySchema])
    const gateBeingEdited = this.store.createRecord(schemas[0], entityDefaultValue)

    this.setState({
      gateBeingEdited,
      isCreateGateSheetOpen: true,
    })
  }

  private handleCloseCreateGateSheet = (_newGate?: Entity | null) => {
    this.setState({ isCreateGateSheetOpen: false })
    this.loadGates()
  }
}
